import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import CloseIcon from '@mui/icons-material/Close'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, IconButton, Portal, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ImageField, TextField, useRecordContext, useReference } from 'react-admin'

import { INCIDENT_REPORT_ACTION_CONFIRM } from '../../../config/damages'
import { BlueprintReferenceInput } from '../form'
import IncidentCategorySelectInput from '../IncidentCategorySelectInput'
import MarkerInput from '../input'
import OwnershipSelectInput from '../OwnershipSelectInput'
import SeveritySelectInput from '../SeveritySelectInput'
import {
  ActionCommentSection,
  ActionModalSection,
  CONFIRM_I18N_PREFIX,
  CONFIRM_SECTIONS_I18N_PREFIX,
  IncidentReportActionDialog,
  useActionSubmit,
} from './common'

const ConfirmInitialPicture = ({ onOpenPreview }) => {
  const record = useRecordContext()
  if (!record.picture) return null

  return (
    <Box
      onClick={onOpenPreview}
      sx={{
        height: '100%',
        minHeight: { xs: 260, md: '100%' },
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'zoom-in',
        transition: 'opacity 120ms ease',
        '&:hover': { opacity: 0.92 },
        '& .RaImageField-image': {
          height: '100%',
          maxHeight: 'none',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
          margin: 0,
          borderRadius: 1,
        },
      }}
    >
      <ImageField source="picture" record={record} />
    </Box>
  )
}

const ConfirmInitialDescription = () => {
  const translate = useTranslate()

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        {translate(`${CONFIRM_SECTIONS_I18N_PREFIX}.initial.reporterDescription`)}
      </Typography>
      <TextField placeholder source="note" emptyText={translate('resources.incident-reports.show.note.fallback')} />
    </Box>
  )
}

const InitialSection = ({ onOpenPreview }) => {
  const record = useRecordContext()
  const hasPicture = Boolean(record.picture)

  return (
    <ActionModalSection
      titleKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.initial.title`}
      titleIcon={CameraAltOutlinedIcon}
      rightElement={hasPicture ? <ConfirmInitialPicture onOpenPreview={onOpenPreview} /> : null}
    >
      <ConfirmInitialDescription />
    </ActionModalSection>
  )
}

const ConfirmQualificationFields = () => {
  const record = useRecordContext()
  const { referenceRecord: vehicle } = useReference({ reference: 'vehicles', id: record.vehicle_id })

  if (!vehicle) return null

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        columnGap: 2,
        rowGap: 1,
        alignItems: 'start',
        my: 2,
        width: '100%',
        '& .MuiFormControl-root': { m: 0, width: '100%' },
      }}
    >
      <IncidentCategorySelectInput
        nfcAccessSupported={Boolean(vehicle.nfc_access_supported)}
        includeBackendManaged={false}
        sx={{ width: '100%' }}
      />
      <SeveritySelectInput sx={{ width: '100%' }} />
      <OwnershipSelectInput sx={{ width: '100%' }} />
    </Box>
  )
}

const ConfirmQualificationLocationLeft = () => {
  const translate = useTranslate()

  return (
    <Box sx={{ minWidth: 0, '& .MuiFormControl-root': { m: 0, width: '100%', maxWidth: 190 } }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
        {translate(`${CONFIRM_SECTIONS_I18N_PREFIX}.location.title`)}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', mb: 0.75, display: 'block', whiteSpace: 'nowrap', overflow: 'visible' }}
      >
        {translate(`${CONFIRM_SECTIONS_I18N_PREFIX}.location.helper`)}
      </Typography>
      <BlueprintReferenceInput readOnly={false} />
    </Box>
  )
}

const ConfirmQualificationLocationCenter = () => (
  <Box
    sx={{
      minWidth: 0,
      height: '95px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      '& > .MuiBox-root': {
        ml: '0 !important',
        mt: '0 !important',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& > .MuiBox-root > div': {
        transform: 'scale(0.74)',
        transformOrigin: 'center',
      },
    }}
  >
    <MarkerInput onNewMarker={() => {}} />
  </Box>
)

const ConfirmQualificationLocationHint = () => {
  const translate = useTranslate()

  return (
    <Box
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.12)' : '#eef8ff'),
        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.35)' : '#cbe8fb'}`,
        borderRadius: 2,
        px: 1.25,
        py: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        alignSelf: { xs: 'stretch', md: 'center' },
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: (theme) => theme.palette.info.main, mt: 0.1 }} />
      <Typography variant="caption" sx={{ lineHeight: 1.4, color: 'text.secondary', minWidth: 0 }}>
        {translate(`${CONFIRM_SECTIONS_I18N_PREFIX}.location.interactiveHint`)}
      </Typography>
    </Box>
  )
}

const ConfirmQualificationLocationPanel = () => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      p: 1.5,
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        md: 'minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)',
      },
      gap: 1.5,
      alignItems: 'center',
      width: '100%',
      overflow: 'hidden',
    }}
  >
    <ConfirmQualificationLocationLeft />
    <ConfirmQualificationLocationCenter />
    <ConfirmQualificationLocationHint />
  </Box>
)

const QualificationSection = () => (
  <ActionModalSection
    titleKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.qualification.title`}
    titleIcon={FactCheckOutlinedIcon}
    helperKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.qualification.helper`}
  >
    <ConfirmQualificationFields />
    <ConfirmQualificationLocationPanel />
  </ActionModalSection>
)

export const ConfirmDamageReportFormDialog = ({ open, onClose }) => {
  const record = useRecordContext()
  const [isInitialReportImagePreviewOpen, setIsInitialReportImagePreviewOpen] = useState(false)
  const [previewOriginRect, setPreviewOriginRect] = useState(null)
  const [previewTransform, setPreviewTransform] = useState('translate(0px, 0px) scale(1)')
  const previewImageContainerRef = useRef(null)
  const [submitAction, isLoading] = useActionSubmit(record.id, onClose)
  const onSubmitAction = (values) =>
    submitAction(
      INCIDENT_REPORT_ACTION_CONFIRM,
      {
        comment: values.comment,
        details: Object.fromEntries(Object.entries(values).filter(([key]) => key !== 'comment')),
      },
      'resources.incident-reports.actions.confirm.success',
    )

  useLayoutEffect(() => {
    if (!isInitialReportImagePreviewOpen || !previewOriginRect || !previewImageContainerRef.current) return

    const targetRect = previewImageContainerRef.current.getBoundingClientRect()
    const originCenterX = previewOriginRect.left + previewOriginRect.width / 2
    const originCenterY = previewOriginRect.top + previewOriginRect.height / 2
    const targetCenterX = targetRect.left + targetRect.width / 2
    const targetCenterY = targetRect.top + targetRect.height / 2
    const deltaX = originCenterX - targetCenterX
    const deltaY = originCenterY - targetCenterY
    const scaleX = previewOriginRect.width / targetRect.width
    const scaleY = previewOriginRect.height / targetRect.height

    setPreviewTransform(`translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`)
    requestAnimationFrame(() => setPreviewTransform('translate(0px, 0px) scale(1)'))
  }, [isInitialReportImagePreviewOpen, previewOriginRect])

  useEffect(() => {
    if (!isInitialReportImagePreviewOpen) return undefined

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setIsInitialReportImagePreviewOpen(false)
    }

    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [isInitialReportImagePreviewOpen])

  const handleOpenPreview = (event) => {
    setPreviewOriginRect(event.currentTarget.getBoundingClientRect())
    setIsInitialReportImagePreviewOpen(true)
  }

  return (
    <>
      <IncidentReportActionDialog
        open={open}
        onClose={onClose}
        isLoading={isLoading}
        i18nPrefix={CONFIRM_I18N_PREFIX}
        dialogProps={{ disableEscapeKeyDown: isInitialReportImagePreviewOpen }}
        footerProps={{
          confirmLabelKey: `${CONFIRM_I18N_PREFIX}.confirmCta`,
          onSubmitAction,
        }}
      >
        <InitialSection onOpenPreview={handleOpenPreview} />
        <QualificationSection />
        <ActionCommentSection
          titleKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.validationNote.title`}
          helperKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.validationNote.helper`}
          placeholderKey={`${CONFIRM_SECTIONS_I18N_PREFIX}.validationNote.inputPlaceholder`}
          sx={{ mt: 0 }}
        />
      </IncidentReportActionDialog>
      {isInitialReportImagePreviewOpen && record.picture && (
        <Portal>
          <Box
            onClick={() => setIsInitialReportImagePreviewOpen(false)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Box
              onClick={(event) => event.stopPropagation()}
              ref={previewImageContainerRef}
              sx={{
                position: 'relative',
                width: 'min(92vw, 1200px)',
                height: 'min(90vh, 1000px)',
                overflow: 'hidden',
                borderRadius: 1,
                boxShadow: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: previewTransform,
                transition: 'transform 240ms cubic-bezier(0.2, 0.7, 0.1, 1)',
              }}
            >
              <IconButton
                onClick={() => setIsInitialReportImagePreviewOpen(false)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.92)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={record.picture}
                alt="incident report"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>
        </Portal>
      )}
    </>
  )
}
